import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { resetPassword } from '../../../../api';
import Swal from 'sweetalert2';
import qs from 'qs';

interface LocationState {
  email: string;
}

const OTPVerifySchema = Yup.object().shape({
  otp: Yup.string().required('OTP is required').length(6, 'OTP must be 6 digits'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/\d/, 'Password must contain at least one number')
    .matches(/[@$!%*?&#^(){}[\]/\\.,<>;:'"`~|_+=-]/, 'Password must contain at least one special character')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm Password is required'),
});

export function OTPVerify() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setConfirmShowPassword] = useState(false);
  const navigate = useNavigate();
  const { state } = useLocation() as { state: LocationState };
  const email = state?.email || '';

  const formik = useFormik({
    initialValues: {
      otp: '',
      email: email,
      password: '',
      confirmPassword: '',
    },
    validationSchema: OTPVerifySchema,        
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      try {
        const data = {
          type: 'email',
          email: values.email,
          code: values.otp,
          password: values.password,
        };
        const response = await resetPassword(data);
        
        Swal.fire({
          title: 'Your password has been updated successfully.',
          icon: 'success',
          confirmButtonText: 'OK',
        }).then(() => {
          navigate('/auth/login');
        });  
      } catch (error: any) {
        Swal.fire({
          title: error.response.data.detail,
          icon: 'error',
          confirmButtonText: 'OK',
        });
      } finally {
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    const handlePopState = () => {
      navigate('/auth/login', { replace: true });
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [navigate]);

  return (
    <div className='d-flex flex-column flex-root vh-100'>
      <div className='d-flex flex-column flex-lg-row h-100 w-100'>
        <div
          className='d-flex align-items-center justify-content-center w-100'
          style={{ flex: '7', height: '100vh' }}
        >
          <img
            src='/assets/media/logos/background.jpg'
            alt='signup'
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
        <div
          className='d-flex flex-column justify-content-center align-items-center p-10 w-100'
          style={{ flex: '3', minHeight: '100vh', maxHeight: '50vh', overflowY: 'auto' }}
        >
          <div
            className='fixed'
            style={{
              top: '10%',
              right: '160px',
              transform: 'translateY(-50%)',
              paddingBottom: '4%',
              zIndex: 1000,
            }}
          >
            <img
              alt='logo'
              src='/assets/media/logos/mapmycrop.png'
              style={{
                width: '200px',
                height: '50px',
              }}
            />
          </div>
          <div
            className='bg-body d-flex flex-column flex-center rounded-4 p-10 w-100'
            style={{
              maxWidth: '450px',
              width: '100%',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            }}
          >
            <div className='d-flex flex-center flex-column align-items-stretch w-100'>
              <h1 className='text-dark fw-bolder mb-5'>Verify OTP & Reset Password</h1>
              <form className='w-100' onSubmit={formik.handleSubmit} noValidate>
                <div className='fv-row mb-8'>
                  <input
                    type='text'
                    {...formik.getFieldProps('otp')}
                    className='form-control bg-transparent'
                    placeholder='Enter 6-digit OTP'
                    maxLength={6}
                  />
                  {formik.touched.otp && formik.errors.otp && (
                    <div className='fv-plugins-message-container'>
                      <span role='alert' className='text-danger'>
                        {formik.errors.otp}
                      </span>
                    </div>
                  )}
                </div>
                <div className='fv-row mb-8 position-relative'>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...formik.getFieldProps('password')}
                    className='form-control bg-transparent'
                    placeholder='Enter new password'
                  />
                  {formik.touched.password && formik.errors.password && (
                    <div className='fv-plugins-message-container'>
                      <span role='alert' className='text-danger'>
                        {formik.errors.password}
                      </span>
                    </div>
                  )}
                  {formik.values.password && (
                    <span
                      className='position-absolute top-50 translate-middle-y'
                      style={{ right: '10px', cursor: 'pointer' }}
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEye /> : <FaEyeSlash />}
                    </span>
                  )}
                </div>
                <div className='fv-row mb-8 position-relative'>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...formik.getFieldProps('confirmPassword')}
                    className='form-control bg-transparent'
                    placeholder='Confirm your password'
                  />
                  {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                    <div className='fv-plugins-message-container'>
                      <span role='alert' className='text-danger'>
                        {formik.errors.confirmPassword}
                      </span>
                    </div>
                  )}
                  {formik.values.confirmPassword && (
                    <span
                      className='position-absolute top-50 translate-middle-y'
                      style={{ right: '10px', cursor: 'pointer' }}
                      onClick={() => setConfirmShowPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                    </span>
                  )}
                </div>
                <div className='d-flex justify-content-between'>
                  <button type='submit' className='btn btn-primary w-100' disabled={loading}>
                    {loading ? (
                      <>
                        Please wait...{' '}
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </>
                    ) : (
                      'Update Password'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}